// Impuestos de una factura timbrada por el personal.
//
// El monto que se captura es lo que pagó el cliente (total neto). Para una
// persona moral (RFC de 12 caracteres) la Academia (RESICO, régimen 626) traslada
// IVA y el cliente le retiene IVA e ISR, así que el subtotal se calcula hacia
// atrás:
//
//     subtotal = total / (1 + IVA − retIVA − retISR)
//
// Ejemplo real: 2,700.00 → subtotal 2,594.08, IVA 415.05, ret IVA 276.70,
// ret ISR 32.43.
//
// La función de Netlify (netlify/functions/facturapi.js) no recalcula: recibe
// el subtotal ya calculado aquí y lo pasa a Facturapi con tax_included: false.

export interface InvoiceTaxConfig {
	ivaPct: number; // IVA trasladado, en %
	withholdings: boolean; // ¿Lleva retenciones?
	retIvaPct: number; // Retención de IVA, en %
	retIsrPct: number; // Retención de ISR, en %
}

export interface InvoiceBreakdown {
	subtotal: number;
	iva: number;
	retIva: number;
	retIsr: number;
	total: number;
}

export interface FacturapiTax {
	type: "IVA" | "ISR";
	rate: number;
	withholding?: boolean;
}

export const DEFAULT_IVA_PCT = 16;
export const DEFAULT_RET_IVA_PCT = 10.6667;
export const DEFAULT_RET_ISR_PCT = 1.25;

export function isPersonaMoral(rfc: string | undefined) {
	return (rfc ?? "").trim().length === 12;
}

export function defaultTaxConfig(rfc: string | undefined): InvoiceTaxConfig {
	return {
		ivaPct: DEFAULT_IVA_PCT,
		withholdings: isPersonaMoral(rfc),
		retIvaPct: DEFAULT_RET_IVA_PCT,
		retIsrPct: DEFAULT_RET_ISR_PCT,
	};
}

const round2 = (x: number) => Math.round((x + Number.EPSILON) * 100) / 100;
// Facturapi / SAT piden la tasa con hasta 6 decimales (10.6667 % → 0.106667).
const pctToRate = (pct: number) => Number((pct / 100).toFixed(6));

function rates(cfg: InvoiceTaxConfig) {
	return {
		iva: pctToRate(cfg.ivaPct || 0),
		retIva: cfg.withholdings ? pctToRate(cfg.retIvaPct || 0) : 0,
		retIsr: cfg.withholdings ? pctToRate(cfg.retIsrPct || 0) : 0,
	};
}

function breakdownFromSubtotal(subtotal: number, cfg: InvoiceTaxConfig): InvoiceBreakdown {
	const r = rates(cfg);
	const iva = round2(subtotal * r.iva);
	const retIva = round2(subtotal * r.retIva);
	const retIsr = round2(subtotal * r.retIsr);
	return { subtotal, iva, retIva, retIsr, total: round2(subtotal + iva - retIva - retIsr) };
}

// Calcula el desglose a partir de lo que pagó el cliente. Si el redondeo deja
// el total a un centavo de lo cobrado, prueba el subtotal un centavo arriba o
// abajo para que el total de la factura sea exactamente lo cobrado.
export function computeBreakdown(total: number, cfg: InvoiceTaxConfig): InvoiceBreakdown | null {
	if (!(total > 0)) return null;
	const r = rates(cfg);
	const factor = 1 + r.iva - r.retIva - r.retIsr;
	if (!(factor > 0)) return null;
	const target = round2(total);
	const base = round2(target / factor);
	const candidates = [base, round2(base - 0.01), round2(base + 0.01)];
	for (const subtotal of candidates) {
		const b = breakdownFromSubtotal(subtotal, cfg);
		if (Math.round(b.total * 100) === Math.round(target * 100)) return b;
	}
	return breakdownFromSubtotal(base, cfg);
}

// El personal puede capturar lo cobrado (total) o el subtotal (antes de impuestos).
export type AmountMode = "total" | "subtotal";

export interface InvoiceAmountChoice {
	mode: AmountMode; // "total": lo cobrado; "subtotal": antes de impuestos
	amount: number;
}

export function computeBreakdownFor(mode: AmountMode, amount: number, cfg: InvoiceTaxConfig): InvoiceBreakdown | null {
	if (mode === "total") return computeBreakdown(amount, cfg);
	if (!(amount > 0)) return null;
	return breakdownFromSubtotal(round2(amount), cfg);
}

// Persona física "como siempre": solo IVA 16 % incluido, sin retenciones.
// En ese caso se manda a Facturapi exactamente lo mismo que antes.
export function isLegacyTaxConfig(cfg: InvoiceTaxConfig) {
	return !cfg.withholdings && Number(cfg.ivaPct) === DEFAULT_IVA_PCT;
}

export function toFacturapiTaxes(cfg: InvoiceTaxConfig): FacturapiTax[] {
	const r = rates(cfg);
	const taxes: FacturapiTax[] = [{ type: "IVA", rate: r.iva }];
	if (r.retIva > 0) taxes.push({ type: "IVA", rate: r.retIva, withholding: true });
	if (r.retIsr > 0) taxes.push({ type: "ISR", rate: r.retIsr, withholding: true });
	return taxes;
}

export function validateTaxConfig(cfg: InvoiceTaxConfig): string | null {
	const inRange = (x: number, max: number) => Number.isFinite(x) && x >= 0 && x <= max;
	if (!inRange(cfg.ivaPct, 16)) return "El IVA debe estar entre 0 y 16 %.";
	if (cfg.withholdings) {
		if (!inRange(cfg.retIvaPct, 16)) return "La retención de IVA debe estar entre 0 y 16 %.";
		if (!inRange(cfg.retIsrPct, 35)) return "La retención de ISR debe estar entre 0 y 35 %.";
	}
	return null;
}

// Campos del concepto para Facturapi: precio (subtotal) y, si aplica, impuestos.
export function facturapiPriceFields(total: number, cfg: InvoiceTaxConfig) {
	if (isLegacyTaxConfig(cfg)) return { price: total };
	const b = computeBreakdown(total, cfg);
	if (!b) throw new Error("No se pudo calcular el desglose");
	return { price: b.subtotal, tax_included: false, taxes: toFacturapiTaxes(cfg) };
}

export function facturapiPriceFieldsFor(mode: AmountMode, amount: number, cfg: InvoiceTaxConfig) {
	if (mode === "total") return facturapiPriceFields(amount, cfg);
	const b = computeBreakdownFor(mode, amount, cfg);
	if (!b) throw new Error("No se pudo calcular el desglose");
	return { price: b.subtotal, tax_included: false, taxes: toFacturapiTaxes(cfg) };
}

export const money = (x: number) =>
	x.toLocaleString("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 });

export function breakdownText(b: InvoiceBreakdown, cfg: InvoiceTaxConfig) {
	const lines = [`Subtotal: ${money(b.subtotal)}`, `IVA ${cfg.ivaPct} %: ${money(b.iva)}`];
	if (cfg.withholdings) {
		lines.push(`Retención IVA ${cfg.retIvaPct} %: −${money(b.retIva)}`);
		lines.push(`Retención ISR ${cfg.retIsrPct} %: −${money(b.retIsr)}`);
	}
	lines.push(`Total: ${money(b.total)}`);
	return lines.join("\n");
}
